import { useEffect, useState } from "react";
import { Formik, ErrorMessage, Field, FieldArray } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { FaTrashAlt } from "react-icons/fa";
import {
  ContainerForm,
  ContainerInput,
  ErrorStyled,
  ContainerButton,
  InputStyled,
  ContainerInputTel,
  InputStyledTel,
  SelectStyledTel,
  ButtonTelTrash,
  ButtonTel,
  SectionTitle,
} from "../../components/formulario/formularioStyles";
import ButtonForm from "../../components/buttonForm/ButtonForm";
import BackButton from "../../components/backButton/BackButton";
import Modal from "../../components/modal/Modal";
import { logout } from "../../redux/user/userSlice";
import { initialValuesSolicitud } from "../../formik/initialValues";
import { validationSolicitud } from "../../formik/validationSchema";
import { useEnvioUnico } from "../../hook/useEnvioUnico";
import {
  nuevoId,
  pedidoBackend,
  registrarSolicitud,
  registrarEvento,
} from "../../registro/registro";

const espera = (ms) => new Promise((r) => setTimeout(r, ms));

// Solicitud de servicio: prospecto en una zona donde hoy no hay servicio. A
// diferencia de la venta, NO va a Iptel (no hay sucursal ni plan): su única
// copia vive en el backend. Por eso se manda directo para poder confirmarla y,
// si el backend no responde, se encola y se reintenta (mismo origenId).
const SolicitudServicio = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const token = currentUser?.data?.token || false;

  const [submitted, setSubmitted] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [envioOk, setEnvioOk] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const { enviando, ejecutar: enviarUnaVez } = useEnvioUnico();

  // Requiere sesión; si el token venció, cierra sesión (igual que el formulario).
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    let timeoutId;
    const vencer = () => {
      registrarEvento("web.sesion-vencida", "Sesión vencida en solicitud de servicio");
      dispatch(logout());
      navigate("/");
    };
    try {
      const restante = jwtDecode(token).exp * 1000 - Date.now();
      if (restante <= 0) vencer();
      else timeoutId = setTimeout(vencer, restante);
    } catch {
      vencer();
    }
    return () => clearTimeout(timeoutId);
  }, [token, navigate, dispatch]);

  const reset = () => {
    setSubmitted(false);
    setMensaje("");
    setEnvioOk(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const enviar = (values) =>
    enviarUnaVez(async () => {
      const telefonos = (values.telefonos || [])
        .filter((t) => t && t.numero)
        .map((t) => ({ tipo: t.tipo, numero: t.numero.trim() }));

      const body = {
        fechaSolicitud: new Date().toISOString(),
        nombre: values.nombre.trim(),
        apellido: values.apellido.trim(),
        dni: values.documento?.trim() || null,
        email: values.email?.trim() || null,
        genero: values.genero || null,
        telefono: telefonos[0]?.numero || "",
        telefonos,
        provincia: values.provincia.trim(),
        localidad: values.localidad.trim(),
        calle: values.calle?.trim() || null,
        altura: values.altura?.trim() || null,
        piso: values.piso?.trim() || null,
        dpto: values.dpto?.trim() || null,
        referencia: values.referencia?.trim() || null,
      };

      const origenId = nuevoId();
      const exito = (texto) => {
        setMensaje(texto);
        setEnvioOk(true);
      };

      try {
        await pedidoBackend("put", `/log/solicitudes/${origenId}`, { datos: body });
        exito("¡Solicitud registrada!");
        await espera(700);
        setSubmitted(true);
      } catch (e) {
        const st = e?.response?.status;
        if (st && st >= 400 && st < 500 && st !== 429) {
          // El backend la rechazó: mostrar el error real, no encolar.
          setModalMessage(
            e?.response?.data?.error || "No se pudo registrar la solicitud.",
          );
          setModalOpen(true);
        } else {
          // Backend caído / sin conexión / error temporal: se encola y reintenta.
          registrarSolicitud(origenId, body);
          exito(
            "Solicitud recibida. Se registrará apenas se restablezca la conexión.",
          );
          await espera(700);
          setSubmitted(true);
        }
      }
    });

  if (submitted) {
    return (
      <Modal isOpen={true} onClose={reset}>
        <h2>{mensaje}</h2>
      </Modal>
    );
  }

  return (
    <>
      <Formik
        initialValues={initialValuesSolicitud}
        validationSchema={validationSolicitud}
        onSubmit={enviar}
      >
        {({ values, resetForm }) => (
          <ContainerForm>
            <BackButton
              confirmMessage="¿Seguro que querés salir? Vas a perder los datos cargados."
              onBeforeNavigate={() => {
                registrarEvento("formulario.salida", "Salió de solicitud de servicio");
                resetForm();
              }}
            />
            <h2>Solicitud de servicio</h2>
            <p style={{ color: "var(--grisMedio)", fontSize: "0.9rem", margin: "0 0 0.5rem" }}>
              Para zonas donde todavía no hay servicio. Se registra el interés y
              la ubicación; no se ofrece plan.
            </p>

            <SectionTitle>Datos personales</SectionTitle>
            <ContainerInput>
              <label>Nombre:</label>
              <Field name="nombre" type="text" as={InputStyled} placeholder="Nombre" />
            </ContainerInput>
            <ErrorMessage name="nombre" component={ErrorStyled} />

            <ContainerInput>
              <label>Apellido:</label>
              <Field name="apellido" type="text" as={InputStyled} placeholder="Apellido" />
            </ContainerInput>
            <ErrorMessage name="apellido" component={ErrorStyled} />

            <ContainerInput>
              <label>DNI (opcional):</label>
              <Field name="documento" type="text" as={InputStyled} placeholder="Ej. 22222222" />
            </ContainerInput>
            <ErrorMessage name="documento" component={ErrorStyled} />

            <ContainerInput>
              <label>Género (opcional):</label>
              <Field as="select" name="genero">
                <option value="">Seleccione el género</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </Field>
            </ContainerInput>
            <ErrorMessage name="genero" component={ErrorStyled} />

            <ContainerInput>
              <label>Email (opcional):</label>
              <Field name="email" type="email" as={InputStyled} placeholder="Ej. correo@ejemplo.com" />
            </ContainerInput>
            <ErrorMessage name="email" component={ErrorStyled} />

            <FieldArray name="telefonos">
              {({ push, remove }) => (
                <div>
                  {values.telefonos.map((telefono, index) => (
                    <div key={index}>
                      <ContainerInputTel>
                        <label>Tipo:</label>
                        <Field as={SelectStyledTel} name={`telefonos[${index}].tipo`}>
                          <option value="">Seleccione</option>
                          <option value="cel">Celular</option>
                          <option value="fijo">Fijo</option>
                          <option value="fax">Fax</option>
                          <option value="otro">Otro</option>
                        </Field>
                        <label>Número:</label>
                        <Field
                          name={`telefonos[${index}].numero`}
                          type="tel"
                          as={InputStyledTel}
                          placeholder="Ej. 3512345678"
                        />
                        {index > 0 && (
                          <ButtonTelTrash type="button" onClick={() => remove(index)}>
                            <FaTrashAlt />
                          </ButtonTelTrash>
                        )}
                      </ContainerInputTel>
                      <ErrorMessage name={`telefonos[${index}].tipo`} component={ErrorStyled} />{" "}
                      <ErrorMessage name={`telefonos[${index}].numero`} component={ErrorStyled} />
                    </div>
                  ))}
                  <ButtonTel type="button" onClick={() => push({ tipo: "", numero: "" })}>
                    + Agregar Teléfono
                  </ButtonTel>
                </div>
              )}
            </FieldArray>

            <SectionTitle>Ubicación</SectionTitle>
            <ContainerInput>
              <label>Provincia:</label>
              <Field name="provincia" type="text" as={InputStyled} placeholder="Ej. Córdoba" />
            </ContainerInput>
            <ErrorMessage name="provincia" component={ErrorStyled} />

            <ContainerInput>
              <label>Localidad / barrio:</label>
              <Field name="localidad" type="text" as={InputStyled} placeholder="Ej. Villa del Rosario" />
            </ContainerInput>
            <ErrorMessage name="localidad" component={ErrorStyled} />

            <ContainerInput>
              <label>Calle:</label>
              <Field name="calle" type="text" as={InputStyled} placeholder="Ej. San Martín" />
            </ContainerInput>
            <ErrorMessage name="calle" component={ErrorStyled} />

            <ContainerInput>
              <label>Altura:</label>
              <Field name="altura" type="text" as={InputStyled} placeholder="Ej. 1234" />
            </ContainerInput>
            <ErrorMessage name="altura" component={ErrorStyled} />

            <ContainerInput>
              <label>Piso:</label>
              <Field name="piso" type="text" as={InputStyled} placeholder="Ej. 1" />
            </ContainerInput>
            <ErrorMessage name="piso" component={ErrorStyled} />

            <ContainerInput>
              <label>Dpto:</label>
              <Field name="dpto" type="text" as={InputStyled} placeholder="Ej. E" />
            </ContainerInput>
            <ErrorMessage name="dpto" component={ErrorStyled} />

            <ContainerInput>
              <label>Referencia:</label>
              <Field name="referencia">
                {({ field }) => (
                  <div style={{ width: "100%" }}>
                    <textarea
                      {...field}
                      id="referencia"
                      placeholder="Entre calles, barrio, cómo llegar, etc."
                      maxLength={500}
                      style={{
                        resize: "none",
                        width: "100%",
                        height: "100px",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1.5px solid #ddd",
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: "0.88rem",
                        color: "var(--grisOscuro)",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ fontSize: "12px", color: "#888", textAlign: "right", marginTop: "4px" }}>
                      {field.value?.length || 0}/500
                    </div>
                  </div>
                )}
              </Field>
            </ContainerInput>
            <ErrorMessage name="referencia" component={ErrorStyled} />

            <ContainerButton>
              <ButtonForm
                type="submit"
                disabled={submitted || enviando}
                progreso={envioOk ? "ok" : enviando ? "enviando" : undefined}
              >
                {envioOk ? "✓ Registrada" : enviando ? "Enviando..." : "Registrar solicitud"}
              </ButtonForm>
            </ContainerButton>
            <button
              onClick={() => {
                registrarEvento("formulario.limpiar", "Limpió la solicitud de servicio");
                resetForm();
              }}
              className="botonReset"
              type="button"
            >
              Limpiar Formulario
            </button>
          </ContainerForm>
        )}
      </Formik>
      {modalOpen && (
        <Modal isOpen={true} onClose={() => setModalOpen(false)}>
          <h2>{modalMessage}</h2>
        </Modal>
      )}
    </>
  );
};

export default SolicitudServicio;
